import { useMemo } from 'react'

import { DashCard } from './DashboardCards'
import { FUNNEL_STEPS } from '../../../lib/coverage'
import { worstCoverageMandals, type MandalCoverageRow } from '../../../lib/dashboard-insights'
import { SITE_STATUS_META } from '../../../lib/lens'
import { prettyName } from '../../../lib/mandal-name'

import type { MandalAggregate } from '../../../api/types'

const TOP_N = 8

/** The per-status count of a row, keyed by the funnel step's bucket name. */
const bucketOf: Record<(typeof FUNNEL_STEPS)[number]['bucket'], (r: MandalCoverageRow) => number> = {
  surveyed: (r) => r.surveyed,
  inProgress: (r) => r.inProgress,
  overdue: (r) => r.overdue,
  never: (r) => r.never,
}

interface MandalCoverageCardProps {
  /** Mandal aggregates in the current scope (already mandal-filtered by the toolbar). */
  mandals: MandalAggregate[]
  /** Click a row → jump to the Coverage board scoped to that mandal. */
  onPickMandal: (name: string) => void
  onOpen: () => void
}

/**
 * "Coverage by mandal" — every mandal's survey-status mix as one stacked bar, worst-covered first,
 * so the geography of the coverage gap is readable in one glance. The four colours are the same
 * ones the donut, the map dots and the Coverage board use. Clicking a row opens the Coverage board
 * scoped to that mandal.
 */
export function MandalCoverageCard({ mandals, onPickMandal, onOpen }: MandalCoverageCardProps) {
  const { rows, more } = useMemo(() => worstCoverageMandals(mandals, TOP_N), [mandals])

  return (
    <DashCard title="Coverage by mandal" caption="Worst covered first" onOpen={onOpen} openLabel="Open Coverage board">
      {rows.length === 0 ? (
        <div className="py-4 text-center text-[11.5px] text-gis-soft">No mapped sites in this selection.</div>
      ) : (
        <>
          {/* Shared status legend — identity is label + colour, never colour alone. */}
          <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1">
            {FUNNEL_STEPS.map((s) => (
              <span key={s.key} className="flex items-center gap-[5px] text-[10px] font-medium text-gis-soft">
                <span className="h-[8px] w-[8px] rounded-[3px]" style={{ background: SITE_STATUS_META[s.key].color }} />
                {s.label}
              </span>
            ))}
          </div>
          <div className="flex flex-col gap-[6px]">
            {rows.map((r) => (
              <button
                key={r.name}
                type="button"
                onClick={() => onPickMandal(r.name)}
                title={`Open the Coverage board scoped to ${prettyName(r.name)}`}
                className="-mx-1 grid cursor-pointer grid-cols-[minmax(0,128px)_minmax(0,1fr)_auto] items-center gap-3 rounded-[8px] px-1 py-[3px] text-left transition-colors hover:bg-[#f5f7fa] focus-visible:ring-2 focus-visible:ring-blue-500/60"
              >
                <span className="truncate text-[12px] font-semibold text-gis-ink-2">{prettyName(r.name)}</span>
                <span className="flex h-[10px] min-w-0 gap-[2px] overflow-hidden rounded-full bg-gis-track-2">
                  {FUNNEL_STEPS.map((s) => {
                    const n = bucketOf[s.bucket](r)
                    return n > 0 ? (
                      <span
                        key={s.key}
                        className="block h-full transition-[width] duration-[450ms] ease-[cubic-bezier(.2,.7,.3,1)]"
                        style={{ width: `${(n / r.total) * 100}%`, background: SITE_STATUS_META[s.key].color }}
                        title={`${s.label}: ${n}`}
                      />
                    ) : null
                  })}
                </span>
                <span className="flex items-center gap-2 text-[10.5px] tabular-nums">
                  <span className="min-w-[34px] text-right font-bold text-gis-ink">{r.pct}%</span>
                  <span className="min-w-[52px] text-right text-gis-soft">{r.surveyed}/{r.total} sites</span>
                </span>
              </button>
            ))}
          </div>
          {more > 0 && (
            <p className="mt-2 text-[9.5px] text-gis-muted">
              +{more} better-covered mandal{more === 1 ? '' : 's'} — open the Coverage board for all of them.
            </p>
          )}
        </>
      )}
    </DashCard>
  )
}
